import { useEffect, useRef, useState } from 'react';
import type { TaskInput, TaskSummary } from '../../lib/tasks';
import type { TaskStatus } from '../../lib/taskStatus';
import { defaultOwnerId, type Assignee } from '../../lib/assignees';
import { Button, Field, Input, Select, Textarea } from '../ui';
import { TaskFields, type TaskFieldValues } from './TaskFields';
import {
  PanelFooter,
  PanelHeader,
  useSidePanelClose,
  useSidePanelCloseGuard,
} from './SidePanel';
import { PlusIcon } from '../icons';

/**
 * A task that does not exist yet.
 *
 * Everything lives in local state until Create, so closing without saving leaves
 * nothing behind. That is the whole point: the alternative -- posting an empty
 * task on open and autosaving each field, as the detail panel does -- litters the
 * list with "Untitled" rows every time someone changes their mind.
 *
 * The cost is that anything needing a task id cannot be here: comments,
 * attachments, time logging, subtasks and prerequisites all appear the moment the
 * task is real, which is one navigation away.
 *
 * Create hands the draft to the page and returns immediately -- it does not wait
 * for the server, and there is deliberately no in-flight state here at all. The
 * request outlives this component (closing the panel unmounts it), so it belongs
 * to whoever owns the list; a failure surfaces there, over the row that had to
 * be taken back.
 */
export function NewTaskForm({
  titleId,
  initialTitle,
  initialStatus,
  tasks,
  assignees,
  tagSuggestions,
  projects,
  initialProjectId = '',
  onCreate,
}: {
  titleId: string;
  /** Carried over from the inline add row, so expanding continues rather than restarts. */
  initialTitle: string;
  initialStatus: TaskStatus;
  /** Candidates for the parent picker. On a cross-project page the caller
   *  narrows this to the chosen project -- see `projectId` below. */
  tasks: TaskSummary[];
  assignees: Assignee[];
  tagSuggestions: string[];
  /**
   * Offered only where the page cannot know the project itself.
   *
   * Inside a project the field would be a select with one right answer, so it
   * is left out entirely and the caller supplies the id. On the Tasks page
   * there is no such default and the question has to be asked.
   */
  projects?: { id: string; name: string }[];
  initialProjectId?: string;
  /** Fire-and-forget: the page posts it, shows it, and reports any failure.
   *  `projectId` is '' wherever the caller already knows it. */
  onCreate: (input: TaskInput, projectId: string) => void;
}) {
  const [projectId, setProjectId] = useState(initialProjectId);
  const [projectError, setProjectError] = useState<string | null>(null);

  /**
   * What the parent picker is allowed to offer.
   *
   * Where the project is being chosen here, the candidates have to follow it:
   * the server rejects a parent from another project outright, so offering the
   * whole workspace would be offering choices it knows will fail. Before a
   * project is picked there is no legal parent at all, and an empty list says
   * so more honestly than a full one that will not save.
   */
  const parentCandidates = projects
    ? tasks.filter((task) => task.projectId === projectId)
    : tasks;
  // Through the shell, so cancelling slides out like every other exit.
  const close = useSidePanelClose();
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState('');

  /** Whoever you starred on the assignee list, usually yourself. */
  const starredOwnerId = defaultOwnerId(assignees);

  const initialValues: TaskFieldValues = {
    status: initialStatus,
    priority: 'MEDIUM',
    tags: [],
    startDate: null,
    dueDate: null,
    remindAt: null,
    repeat: null,
    // Shown, not applied behind the form. The server defaults an owner-less
    // create to the same person -- which is what the inline add row relies on --
    // so filling the control in here is the visible half of one rule rather than
    // a second one.
    ownerId: starredOwnerId,
    collaboratorIds: [],
    parent: null,
  };

  const [values, setValues] = useState<TaskFieldValues>(initialValues);

  /**
   * What the form was last seeded with. The discard prompt measures against this
   * rather than against empty, so neither a title handed over from the inline
   * add row nor a defaulted owner counts as unsaved work -- and after Create +
   * new it is rebased onto whatever the next draft starts from, so the
   * properties deliberately kept do not read as a filled-in form nobody typed.
   */
  const [baseline, setBaseline] = useState<{
    projectId: string;
    title: string;
    description: string;
    values: TaskFieldValues;
  }>({ projectId: initialProjectId, title: initialTitle, description: '', values: initialValues });

  /** Set the moment somebody picks an owner or clears one, so a roster that
   *  lands afterwards cannot overwrite what they did. */
  const ownerTouched = useRef(false);
  /** So Create + new puts the cursor back where the next title goes. */
  const titleRef = useRef<HTMLInputElement>(null);

  const [titleError, setTitleError] = useState<string | null>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  // The page fetches the roster in an effect, so it can arrive after this panel
  // has already mounted -- seeding only in `useState` would leave the owner
  // blank exactly when the panel is opened first thing on a cold page.
  useEffect(() => {
    if (ownerTouched.current || !starredOwnerId || baseline.values.ownerId !== null) return;
    setValues((current) => ({ ...current, ownerId: starredOwnerId }));
    setBaseline((current) => ({
      ...current,
      values: { ...current.values, ownerId: starredOwnerId },
    }));
  }, [starredOwnerId, baseline.values.ownerId]);

  const dirty =
    projectId !== baseline.projectId ||
    title !== baseline.title ||
    description !== baseline.description ||
    values.status !== baseline.values.status ||
    values.priority !== baseline.values.priority ||
    // Compared element by element: `[] !== []` is always true, which would make
    // every fresh draft permanently dirty and pop the discard confirmation on
    // any close.
    !sameIds(values.tags, baseline.values.tags) ||
    values.startDate !== baseline.values.startDate ||
    values.dueDate !== baseline.values.dueDate ||
    values.remindAt !== baseline.values.remindAt ||
    // Compared as JSON: a rule is a small plain object, and the picker builds a
    // fresh one on every change.
    JSON.stringify(values.repeat) !== JSON.stringify(baseline.values.repeat) ||
    values.ownerId !== baseline.values.ownerId ||
    !sameIds(values.collaboratorIds, baseline.values.collaboratorIds) ||
    (values.parent?.id ?? null) !== (baseline.values.parent?.id ?? null);

  /**
   * Nothing here is saved, so closing throws the draft away. Silent for an
   * untouched form, confirmed once there is something to lose -- inline, never
   * `window.confirm`, matching ProjectForm's delete.
   *
   * The gap this cannot cover: the browser Back button discards without asking.
   * Blocking it needs `useBlocker`, which throws outside a data router, and the
   * app is on `<BrowserRouter>`.
   */
  function requestClose() {
    if (!dirty) {
      close();
      return;
    }
    setConfirmingDiscard(true);
  }

  // Escape and the scrim belong to the shell, so the veto is registered there
  // rather than handled here -- otherwise those two paths would discard a filled
  // -in draft while the X button asked first.
  useSidePanelCloseGuard(() => {
    if (!dirty) return false;
    // A second Escape backs out of the confirmation rather than accepting it.
    // Tapping Escape twice out of habit is exactly what someone about to lose a
    // draft would do, so the only way through is the explicit Discard button.
    setConfirmingDiscard((confirming) => !confirming);
    return true;
  });

  function change(patch: Partial<TaskInput>) {
    if ('ownerId' in patch) ownerTouched.current = true;
    // Every key in the patch is also a key of TaskFieldValues except `parentId`,
    // which goes through onParentChange instead.
    setValues((current) => ({ ...current, ...(patch as Partial<TaskFieldValues>) }));
  }

  /**
   * Hand the draft over and either leave or start the next one.
   *
   * `again` keeps the property sheet -- status, priority, owner, dates, tags,
   * parent -- and clears only the title and description. Tasks typed one after
   * another are nearly always the same kind of work on the same person's plate,
   * and re-picking all of it every time is what makes people stop using the
   * button. What is kept is on screen, so nothing is carried over invisibly.
   */
  function submit(next: 'close' | 'again') {
    // Checked before the title so the first thing missing is the first thing
    // named -- the project sits above it in the form.
    if (projects && !projectId) {
      setProjectError('Pick a project for this task.');
      return;
    }

    const trimmed = title.trim();
    if (!trimmed) {
      setTitleError('Give the task a title.');
      titleRef.current?.focus();
      return;
    }

    onCreate({
      title: trimmed,
      description: description.trim() || null,
      status: values.status,
      priority: values.priority,
      tags: values.tags,
      startDate: values.startDate,
      dueDate: values.dueDate,
      remindAt: values.remindAt,
      repeat: values.repeat,
      ownerId: values.ownerId,
      collaboratorIds: values.collaboratorIds,
      parentId: values.parent?.id ?? null,
    }, projectId);

    if (next === 'close') {
      // Forced: the draft has just been handed over, so the unsaved-work guard
      // would be asking about work that is already on its way to the server.
      close({ force: true });
      return;
    }

    setTitle('');
    setDescription('');
    setTitleError(null);
    // Rebased onto what is still on screen, so the kept properties do not arm
    // the discard prompt.
    // The project is kept, not cleared: filing several tasks into the same
    // project is the whole reason Create + new exists on this page.
    setBaseline({ projectId, title: '', description: '', values });
    titleRef.current?.focus();
  }

  return (
    <>
      <PanelHeader onClose={requestClose} closeLabel="Discard this task">
        {/* The one spot of accent in the panel. Without it the header is a line
            of text over a form and reads as a page, not a composer. */}
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-honey-100 text-honey-700">
          <PlusIcon />
        </span>
        {/* `py-0.5` lifts the line to the 28px of the tile beside it, and to the
            same height the detail panel's editable title occupies -- the two
            headers have to measure the same or the contents jump when the panel
            swaps one for the other. Which is exactly what they did: the comment
            claimed they matched while this was `text-base` and the detail's was
            15px. `text-panel-title` is now the one size both take. */}
        <h2
          id={titleId}
          className="min-w-0 flex-1 py-0.5 text-panel-title font-semibold tracking-tight text-stone-900"
        >
          New task
        </h2>
      </PanelHeader>

      {/* `px-4 py-3`, matching TaskDetail and the shared PanelHeader above it.
          This body used to be inset a further four pixels than its own header,
          so the draft and the detail -- the same panel, one after the other --
          did not line up with each other or with their own chrome. */}
      <div className="flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-3">
        {/* Above the title because it is the one answer that changes what the
            rest of the form means: the parent picker below can only offer tasks
            from this project. */}
        {projects ? (
          <Field label="Project" htmlFor="new-task-project" error={projectError}>
            <Select
              id="new-task-project"
              value={projectId}
              onChange={(event) => {
                setProjectId(event.target.value);
                setProjectError(null);
                // A parent from the project you just left would be rejected by
                // the server ("A parent task must be in the same project"), and
                // the picker beneath is about to stop offering it -- so it is
                // dropped here rather than left to fail on Create.
                setValues((current) => ({ ...current, parent: null }));
              }}
            >
              <option value="">Choose a project…</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        {/* What the task *is*, kept together and away from how it is tracked. */}
        <div className="space-y-2">
          <Field label="Title" htmlFor="new-task-title" error={titleError}>
            <Input
              id="new-task-title"
              ref={titleRef}
              // The one field that is always the next thing you want to type,
              // whether the panel was opened empty or from a half-typed row.
              autoFocus
              value={title}
              placeholder="What needs doing?"
              onChange={(event) => {
                setTitle(event.target.value);
                if (titleError) setTitleError(null);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                // Shift held means "and another": the same pairing as the two
                // buttons below, without leaving the keyboard.
                submit(event.shiftKey ? 'again' : 'close');
              }}
            />
          </Field>

          {/* No visible label: it sits directly under the title it elaborates,
              and the placeholder names it. Still named for a screen reader. */}
          <Textarea
            id="new-task-description"
            aria-label="Description"
            rows={3}
            value={description}
            placeholder="Add a description…"
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>

        <TaskFields
          idPrefix="new"
          values={values}
          tasks={parentCandidates}
          // Nothing to exclude: a task that does not exist yet cannot be its own
          // ancestor, and it has no descendants.
          excludeFromParent={new Set()}
          assignees={assignees}
          tagSuggestions={tagSuggestions}
          onChange={change}
          onParentChange={(picked) =>
            setValues((current) => ({
              ...current,
              parent: picked ? { id: picked.id, title: picked.title } : null,
            }))
          }
        />

        <p className="text-xs text-stone-500">
          Comments, attachments, time and subtasks open up once the task exists.
        </p>
      </div>

      {/* Pinned, not the last thing in the scroller: the form is tall enough on
          a laptop that Create sat below the fold, which reads as a form with no
          way to submit it. */}
      <PanelFooter>
        {confirmingDiscard ? (
          <>
            <span className="text-sm text-stone-600">Discard this draft?</span>
            <Button variant="danger" onClick={() => close({ force: true })}>
              Discard
            </Button>
            <Button variant="ghost" autoFocus onClick={() => setConfirmingDiscard(false)}>
              Keep editing
            </Button>
          </>
        ) : (
          <>
            <Button variant="primary" onClick={() => submit('close')}>
              Create task
            </Button>
            <Button
              onClick={() => submit('again')}
              title="Create it and keep the panel open for the next one (Shift+Enter)"
            >
              Create + new
            </Button>
            <Button variant="ghost" onClick={requestClose}>
              Cancel
            </Button>
          </>
        )}
      </PanelFooter>
    </>
  );
}

/** Order matters as much as membership here: both lists are built by appending,
 *  so two sets that differ only in order really are different drafts. */
function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}
